import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiImage, FiEye } from 'react-icons/fi';
import { repairService } from '../../services/repairService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import { formatDate, truncateText } from '../../utils/formatters';
import { toast } from 'react-hot-toast';
import { STATIC_BASE_URL, getImageUrl } from '../../utils/constants'; // Changed this line

const RepairList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const queryClient = useQueryClient();

  const { data: articles, isLoading, error } = useQuery(
    'repairs',
    () => repairService.getAll({ limit: 100 })
  );

  const deleteMutation = useMutation(
    (id) => repairService.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('repairs');
        toast.success('លុបអត្ថបទដោយជោគជ័យ');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចលុបអត្ថបទបានទេ');
      },
    }
  );

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const filteredArticles = articles?.filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="មិនអាចផ្ទុកអត្ថបទ" />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">អត្ថបទ</h1>
        <Link
          to="/admin/repairs/new"
          className="bg-red-600 text-white px-4 py-2 text-sm font-medium flex items-center hover:bg-red-700 transition-colors"
        >
          <FiPlus className="mr-2" /> បន្ថែមអត្ថបទ
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="ស្វែងរកអត្ថបទ..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 focus:outline-none focus:border-red-600"
        />
      </div>

      {/* Articles Table */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">អត្ថបទ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ការពិពណ៌នា</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">អ្នកនិពន្ធ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">រូបភាព</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">បានបង្កើត</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">សកម្មភាព</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredArticles?.map((article) => (
              <tr key={article.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {article.images?.[0] && (
                      <img
                        src={getImageUrl(article.images[0].filepath, 'repairs')}
                        alt={article.title}
                        className="w-10 h-10 object-cover mr-3"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/40x40/F3F4F6/9CA3AF?text=No+Image";
                        }}
                      />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{article.title}</p>
                      <p className="text-xs text-gray-500">
                        ID: {article.id}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {truncateText(article.description?.replace(/<[^>]*>/g, '') || '', 100)}
                  </p>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-600">
                    {article.author || 'អនាមិក'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <FiImage className="mr-1 text-gray-400" />
                    <span className="text-gray-600">{article.images?.length || 0}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(article.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <Link
                      to={`/admin/repairs/${article.id}`}
                      className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                      title="មើល"
                    >
                      <FiEye className="w-4 h-4" />
                    </Link>
                    <Link
                      to={`/admin/repairs/edit/${article.id}`}
                      className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                      title="កែប្រែ"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => setDeleteId(article.id)}
                      className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                      title="លុប"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredArticles?.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">រកមិនឃើញអត្ថបទ</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="លុបអត្ថបទ"
        message="តើអ្នកប្រាកដថាចង់លុបអត្ថបទជួសជុលនេះ? សកម្មភាពនេះមិនអាចត្រឡប់វិញបានទេ។"
        confirmText="លុប"
        cancelText="បោះបង់"
      />
    </div>
  );
};

export default RepairList;